import React, { useState, useEffect } from 'react';

import { isValidEmail } from 'lib/utils';
import { usePOST } from 'hooks/useRequest';

import Form from '../cef_form/form';
import Input from '../cef_form/input';
import CEFMessage from '../cef_message/cef_message';

import UserTestingImg from '../../../assets/images/shared/icons/user-testing.svg';
import warningIconRed from '../../../assets/images/shared/icons/warning-red.png';

const UserPanelForm = ({ setFormStage, userName }) => {
  const [formData, setFormData] = useState({
    name: userName || '',
    email: '',
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
  });

  const [canSubmit, setCanSubmit] = useState(false);

  const {
    data: userPanelData,
    error: requestError,
    loading,
  } = usePOST(canSubmit ? '/user-panel-request' : null, formData);

  useEffect(() => {
    if (userPanelData) {
      setFormStage(3); // Stage 3 = confirmation component
    }
  }, [userPanelData]);

  useEffect(() => {
    if (requestError) {
      setCanSubmit(false);
    }
  }, [requestError]);

  const handleChange = e => {
    const { name, value } = e.target;

    if (name === 'email' && !isValidEmail(value)) {
      setErrors({ ...errors, [name]: 'Please enter a valid email address' });
    } else if (!value) {
      setErrors({ ...errors, [name]: `Please enter your ${name}` });
    } else {
      setErrors({ ...errors, [name]: '' });
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    setCanSubmit(true);
  };

  const isFormValid = () => {
    const { name, email } = formData;

    if (name === '' || email === '' || !isValidEmail(email)) {
      return false;
    }

    return true;
  };

  return (
    <Form onSubmit={handleSubmit} className="cef-feedback-form">
      <div
        className="cef-feedback-form__header cef-feedback-form__header--dark fl ai-c"
        data-testid="user-panel-form">
        <img
          src={UserTestingImg}
          alt=""
          width="70"
          height="70"
          className="mr-075 cef-feedback__user-icon"
        />
        <p className="cef-feedback-form__heading cef-feedback-form__heading--small">
          Join our remote user panel to test new features and contribute to
          improving your experience
        </p>
      </div>

      <div className="cef-feedback-form__wrapper">
        <p className="cef-feedback-form__label m-top-1rem">How it works</p>
        <p className="cef-feedback-form__text mb-20">
          Enter your details below and we will send you information on what will
          be involved in user testing. We will then check with you first to see
          if you would like to opt in. There is no obligation to participate and
          you can leave the user panel at any time.
        </p>

        <Input
          type="text"
          name="name"
          id="name"
          label="Name"
          labelClassName="cef-feedback-form__label"
          inputClassName={`cef-feedback-form__input ${
            errors.name ? 'cef-feedback-form__input--has-error' : ''
          }`}
          errorClassName="cef-form__error"
          value={formData.name}
          handleChange={handleChange}
          error={errors.name}
        />

        <Input
          type="email"
          name="email"
          id="email"
          label="Email"
          labelClassName="cef-feedback-form__label"
          inputClassName={`cef-feedback-form__input ${
            errors.email ? 'cef-feedback-form__input--has-error' : ''
          }`}
          value={formData.email}
          handleChange={handleChange}
          error={errors.email}
        />

        <p className="cef-feedback-form__text cef-feedback-form__text--small mt-20">
          If you would like to see how we handle your data, please see our{' '}
          <a href="/info/privacy_policy" target="_blank">
            privacy notice
          </a>
          .
        </p>
        <p className="cef-feedback-form__text cef-feedback-form__text--small">
          If you do not complete these fields then no further contact will be
          made regarding this.
        </p>

        {requestError && (
          <CEFMessage
            icon={warningIconRed}
            classNames="cef-message--danger cef-message--bold bg-white"
            text="Sorry, your details were not sent. Please try again"
          />
        )}

        <button
          type="submit"
          disabled={!isFormValid() || loading}
          className="mt-20 cef-btn cef-btn--green cef-btn--xl cef-btn--block b lf">
          {loading ? (
            <span className="cef-btn__loader" />
          ) : (
            'Join the User Panel'
          )}
        </button>
      </div>
    </Form>
  );
};

export default UserPanelForm;
